import { Hint, Input } from '@tecsinapse/cortex-react'
import React, { useMemo } from 'react'
import { ErrorMessage } from '@hookform/error-message'
import { useController, useFormContext } from 'react-hook-form'
import clsx from 'clsx'

interface FormTextFieldProps {
  name: string
  label?: string
  customOnBlur?: (e: React.FocusEvent<HTMLDivElement, Element>) => void
  disabled?: boolean
  type?: string
  maxLength?: number
  showCharacterLimit?: boolean
}

export const FormTextField = ({
  name,
  label,
  customOnBlur,
  disabled,
  type,
  maxLength,
  showCharacterLimit
}: FormTextFieldProps) => {
  const {
    formState: { errors, submitCount },
    control,
    register
  } = useFormContext()
  const {
    fieldState: { isTouched, error },
    field: { value }
  } = useController({ name, control })
  const success = isTouched && !error && value?.length > 0

  const length = useMemo(() => (value as string)?.length ?? 0, [value])
  const hasMax = typeof maxLength === 'number'

  return (
    <div className={'w-full'}>
      <div className='flex flex-col gap-micro'>
        <Input.Root
          variants={{
            intent: success ? 'success' : 'default'
          }}
          {...register(name)}
          onBlur={e => {
            register(name).onBlur(e)
            if (customOnBlur) {
              customOnBlur(e)
            }
          }}
          label={label}
          disabled={disabled}
          type={type ?? 'text'}
          maxLength={maxLength}
        />
        {showCharacterLimit ? (
          <p
            className={clsx('text-micro', {
              'text-warning-medium':
                hasMax && length >= maxLength * 0.9 && length < maxLength,
              'text-error-medium': hasMax && length === maxLength
            })}
          >{`${length} / ${maxLength}`}</p>
        ) : null}
      </div>
      {(isTouched || submitCount > 0) && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <Hint variants={{ intent: 'error' }}>{message}</Hint>
          )}
        />
      ) : null}
    </div>
  )
}
